import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './TerminalChipInput.scss';

/**
 * TerminalChipInput — purpose-built recipient input for the terminal
 * composer. Replaces the react-select CreatableSelect in the classic
 * SendEmailForm for the terminal-theme code path.
 *
 * Behaviour (all standard email-composer expectations):
 *   - Commit a chip on Enter, Tab, Space, `,`, `;`, or blur
 *   - Paste "a@b.com, c@d.com; d@e.com" → creates three chips at once
 *   - Backspace on an empty input → removes the last chip
 *   - Click a chip → removes it AND puts its email back in the input
 *     for editing (matches Gmail's behaviour)
 *   - Click the [×] inside a chip → just removes it
 *   - Invalid chips render with an amber border + title attribute
 *     ("not a valid email") — still kept in the list so the user can
 *     fix them
 *   - Autocomplete dropdown filters host-supplied suggestions by
 *     substring match on email or label. Arrow keys navigate, Enter
 *     accepts, Escape closes.
 *
 * The host owns ranking — it passes a pre-ordered `suggestions` array.
 * This component filters by typed substring but preserves ordering
 * within matches so "customer contacts first, then clients, then
 * recent correspondents" comes through as-is.
 */

// ── Helpers ──────────────────────────────────────────────────────────────

// Pragmatic email regex: local@domain.tld. Doesn't try to cover every
// RFC-5321 edge case — just catches the obvious typos. A final SMTP
// round-trip is the only real validator anyway.
const EMAIL_RE = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;
function isValidEmail(s: string): boolean {
  return EMAIL_RE.test(s.trim());
}

/** Split a pasted blob into candidate emails. Accepts , ; whitespace. */
function splitEmails(raw: string): string[] {
  return raw
    .split(/[,;\s]+/g)
    .map(s => s.trim())
    .filter(Boolean);
}

/** Find the best label for an existing value from the suggestion pool. */
function displayLabel(email: string, suggestions: EmailOption[]): string | undefined {
  const match = suggestions.find(s => s.email.toLowerCase() === email.toLowerCase());
  return match?.label;
}

// ─────────────────────────────────────────────────────────────────────────

export interface EmailOption {
  /** The email address (lowercase ideal, case-preserved is fine). */
  email: string;
  /** Optional display name ("John Smith"). Rendered as "John Smith <john@ex.com>". */
  label?: string;
  /** Optional group for visual clustering in the dropdown. Suggestions
   *  are shown in the order the host supplied, so "group" is purely
   *  for visual labels — no re-sorting here. */
  group?: string;
}

export interface TerminalChipInputProps {
  /** Controlled value: list of email addresses currently added as chips. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Host-ranked autocomplete pool. Filtering is substring-only; the
   *  host is expected to have already sorted by preferred priority. */
  suggestions?: EmailOption[];
  placeholder?: string;
  /** aria-label for screen readers. */
  ariaLabel?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Max chip count — useful for single-recipient fields. Default unlimited. */
  max?: number;
  /** Called when the input (not the whole widget) loses focus. */
  onBlur?: () => void;
}

const MAX_DROPDOWN_ITEMS = 8;

const TerminalChipInput: React.FC<TerminalChipInputProps> = ({
  value,
  onChange,
  suggestions = [],
  placeholder = '',
  ariaLabel,
  autoFocus = false,
  disabled = false,
  max,
  onBlur,
}) => {
  const [input, setInput] = useState<string>('');
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [focused, setFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Filtered, deduped suggestion list. Excludes emails already added as
  // chips so the user isn't pestered with duplicates. Capped at
  // MAX_DROPDOWN_ITEMS so long directories don't explode the dropdown.
  const filteredSuggestions = useMemo<EmailOption[]>(() => {
    const q = input.trim().toLowerCase();
    const added = new Set(value.map(v => v.toLowerCase()));
    const results: EmailOption[] = [];
    for (const s of suggestions) {
      if (results.length >= MAX_DROPDOWN_ITEMS) break;
      if (!s.email) continue;
      const eLower = s.email.toLowerCase();
      if (added.has(eLower)) continue;
      if (q) {
        // Coerce label defensively — undefined concatenated here would produce
        // the literal string "undefined" and make every unlabeled suggestion
        // match a user typing that word.
        const haystack = ((s.label || '') + ' ' + s.email).toLowerCase();
        if (!haystack.includes(q)) continue;
      }
      results.push(s);
    }
    return results;
  }, [input, suggestions, value]);

  // Clamp activeIdx when suggestions change
  useEffect(() => {
    if (activeIdx >= filteredSuggestions.length) setActiveIdx(0);
  }, [filteredSuggestions.length, activeIdx]);

  const dropdownOpen = focused && filteredSuggestions.length > 0;

  // ── Mutations ───────────────────────────────────────────────────────
  const atMax = typeof max === 'number' && value.length >= max;

  const addEmail = useCallback((raw: string) => {
    const candidates = splitEmails(raw);
    if (candidates.length === 0) return;
    const existing = new Set(value.map(v => v.toLowerCase()));
    const additions: string[] = [];
    for (const c of candidates) {
      if (existing.has(c.toLowerCase())) continue;
      additions.push(c);
      existing.add(c.toLowerCase());
      if (typeof max === 'number' && value.length + additions.length >= max) break;
    }
    if (additions.length) onChange([...value, ...additions]);
  }, [value, onChange, max]);

  const removeEmailAt = useCallback((idx: number) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  }, [value, onChange]);

  // Click chip → remove AND put back in the input for editing
  const editChip = useCallback((idx: number) => {
    if (disabled) return;
    const email = value[idx];
    removeEmailAt(idx);
    setInput(email);
    window.setTimeout(() => {
      inputRef.current?.focus();
      // Position cursor at end of text
      const el = inputRef.current;
      if (el) { el.selectionStart = el.value.length; el.selectionEnd = el.value.length; }
    }, 0);
  }, [disabled, value, removeEmailAt]);

  // ── Input handlers ──────────────────────────────────────────────────

  const commit = useCallback((raw: string) => {
    addEmail(raw);
    setInput('');
    setActiveIdx(0);
  }, [addEmail]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // Dropdown navigation
    if (dropdownOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(i => Math.min(i + 1, filteredSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setFocused(false);
        return;
      }
    }

    // Enter / Tab / comma / semicolon → commit the current input
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      if (dropdownOpen && filteredSuggestions[activeIdx] && e.key !== 'Tab') {
        // Prefer picking the highlighted suggestion (unless the user
        // pressed Tab, which should commit what they literally typed).
        e.preventDefault();
        commit(filteredSuggestions[activeIdx].email);
        return;
      }
      if (input.trim()) {
        e.preventDefault();
        commit(input);
        return;
      }
      // Nothing typed — let Tab move focus naturally
      return;
    }

    // Space = commit only if the current input looks like a complete email
    if (e.key === ' ' && input.trim() && isValidEmail(input.trim())) {
      e.preventDefault();
      commit(input);
      return;
    }

    // Backspace on empty input → remove last chip
    if (e.key === 'Backspace' && input === '' && value.length > 0) {
      e.preventDefault();
      removeEmailAt(value.length - 1);
      return;
    }
  }, [disabled, dropdownOpen, filteredSuggestions, activeIdx, input, value, commit, removeEmailAt]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const text = e.clipboardData.getData('text');
    // Multi-email paste: split + add directly
    if (/[,;\s]/.test(text)) {
      e.preventDefault();
      addEmail(text);
      setInput('');
    }
    // Single-email paste: let the input receive the text; user can hit
    // Enter to commit
  }, [disabled, addEmail]);

  const handleBlurInput = useCallback(() => {
    // Commit whatever's in the input when focus leaves the widget.
    if (input.trim()) commit(input);
    // Delay hiding the dropdown so a click on a suggestion registers
    // before the element unmounts.
    window.setTimeout(() => setFocused(false), 120);
    onBlur?.();
  }, [input, commit, onBlur]);

  // Pick a suggestion (click or Enter on highlighted)
  const pickSuggestion = useCallback((opt: EmailOption) => {
    commit(opt.email);
    inputRef.current?.focus();
  }, [commit]);

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div
      ref={rootRef}
      className={
        'term-chip-input'
        + (focused ? ' term-chip-input--focused' : '')
        + (disabled ? ' term-chip-input--disabled' : '')
      }
      role="combobox"
      aria-expanded={dropdownOpen}
      aria-haspopup="listbox"
      onClick={() => {
        // Clicking anywhere inside the widget (including empty space)
        // focuses the input so the chip field feels like one big target.
        if (!disabled) inputRef.current?.focus();
      }}
    >
      {value.map((email, idx) => {
        const valid = isValidEmail(email);
        const label = displayLabel(email, suggestions);
        return (
          <span
            key={`${email}-${idx}`}
            className={
              'term-chip'
              + (valid ? '' : ' term-chip--invalid')
            }
            title={valid
              ? (label ? `${label} <${email}>` : email)
              : `not a valid email — click to edit: ${email}`}
          >
            <span
              className="term-chip__label"
              onClick={(e) => { e.stopPropagation(); editChip(idx); }}
              role="button"
              tabIndex={-1}
            >
              {label ? `${label} <${email}>` : email}
            </span>
            <button
              type="button"
              className="term-chip__remove"
              onClick={(e) => { e.stopPropagation(); removeEmailAt(idx); }}
              title="Remove"
              aria-label={`Remove ${email}`}
              tabIndex={-1}
            >
              ×
            </button>
          </span>
        );
      })}

      <input
        ref={inputRef}
        type="text"
        // Email keyboard on phones (@ and . on the primary layer). type stays
        // "text" so commas/semicolons keep committing chips.
        inputMode="email"
        enterKeyHint="done"
        className="term-chip-input__input"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setActiveIdx(0);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={handleBlurInput}
        placeholder={value.length === 0 ? placeholder : ''}
        aria-label={ariaLabel}
        disabled={disabled || atMax}
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
      />

      {/* ── Autocomplete dropdown ──────────────────────────────────── */}
      {dropdownOpen && (
        <div
          ref={listRef}
          className="term-chip-input__menu"
          role="listbox"
          // Prevent blur-before-click from closing the menu before the
          // click handler fires.
          onMouseDown={(e) => e.preventDefault()}
        >
          {filteredSuggestions.map((opt, idx) => (
            <button
              type="button"
              key={`${opt.email}-${idx}`}
              role="option"
              aria-selected={idx === activeIdx}
              className={
                'term-chip-input__option'
                + (idx === activeIdx ? ' term-chip-input__option--active' : '')
              }
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => pickSuggestion(opt)}
            >
              <span className="term-chip-input__option-glyph">
                {idx === activeIdx ? '>' : ' '}
              </span>
              <span className="term-chip-input__option-main">
                {opt.label ? (
                  <>
                    <span className="term-chip-input__option-name">{opt.label}</span>
                    <span className="term-chip-input__option-email">&lt;{opt.email}&gt;</span>
                  </>
                ) : (
                  <span className="term-chip-input__option-email">{opt.email}</span>
                )}
              </span>
              {opt.group && (
                <span className="term-chip-input__option-group">{opt.group}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TerminalChipInput;
